/**
 * CoA Versions Use Cases
 * Business logic for CoA mapping version management
 */

import type { TAppStore } from "../store";
import type {
    TCoAVersion,
    TPaginatedCoAVersions,
    TVersionUploadResult,
    TVersionRestoreResult,
} from "../store";
import { getNow } from "../utils/dateService";

export type TBusinessPartnerType = "service_provider" | "portfolio_company";

async function getAuthHeaders(store: TAppStore): Promise<Record<string, string>> {
    const { tokenService } = await import("@/auth/tokenService");
    const token = await tokenService.getAccessToken();
    const state = store.getState();
    const headers: Record<string, string> = {
        Authorization: `Bearer ${token ?? state.accessToken ?? ""}`,
    };
    const companyId = state.effectiveCompanyId ?? state.user?.companyId;
    if (companyId) {
        headers["X-Company-ID"] = companyId;
    }
    return headers;
}

/** Normalize upload/snapshot API response (snake_case or camelCase) to camelCase for store and UI */
function normalizeVersionUploadResult(raw: Record<string, unknown>): TVersionUploadResult {
    const mappingsCount =
        raw["mappings_count"] !== undefined
            ? Number(raw["mappings_count"])
            : raw["mappingsCount"] !== undefined
              ? Number(raw["mappingsCount"])
              : 0;
    return {
        success: Boolean(raw["success"]),
        errors: Array.isArray(raw["errors"]) ? (raw["errors"] as string[]) : [],
        warnings: Array.isArray(raw["warnings"]) ? (raw["warnings"] as string[]) : [],
        versionId:
            raw["version_id"] != null
                ? String(raw["version_id"])
                : raw["versionId"] != null
                  ? String(raw["versionId"])
                  : null,
        versionNumber:
            raw["version_number"] != null
                ? Number(raw["version_number"])
                : raw["versionNumber"] != null
                  ? Number(raw["versionNumber"])
                  : null,
        mappingsCount,
        storagePath:
            raw["storage_path"] != null
                ? String(raw["storage_path"])
                : raw["storagePath"] != null
                  ? String(raw["storagePath"])
                  : null,
    };
}

async function parseUploadErrorResponse(response: Response): Promise<string> {
    const base = `Upload failed (${response.status} ${response.statusText})`;
    const text = await response.text();
    const withBody = (msg: string) =>
        text.trim() ? `${msg}. Response: ${text.trim().slice(0, 250)}` : msg;

    if (!text.trim()) return base;
    try {
        const body = JSON.parse(text) as Record<string, unknown>;
        const err = body?.["error"] as Record<string, unknown> | undefined;
        if (err?.["message"] && typeof err["message"] === "string") return err["message"];
        const detail = body?.["detail"];
        if (typeof detail === "string") return detail;
        if (detail && typeof detail === "object" && !Array.isArray(detail)) {
            const nestedErr = (detail as Record<string, unknown>)?.["error"] as
                | Record<string, unknown>
                | undefined;
            if (nestedErr?.["message"] && typeof nestedErr["message"] === "string")
                return nestedErr["message"];
        }
        if (Array.isArray(detail) && detail.length > 0) {
            const first = detail[0];
            if (typeof first === "string") return first;
            const firstObj = first as Record<string, unknown> | undefined;
            const msg = firstObj?.["msg"] ?? firstObj?.["message"];
            if (typeof msg === "string") return msg;
        }
        if (typeof body?.["message"] === "string") return body["message"];
        if (
            err?.["details"] &&
            typeof (err["details"] as Record<string, unknown>)?.["message"] === "string"
        )
            return (err["details"] as Record<string, unknown>)["message"] as string;
    } catch {
        if (text.length < 300 && !text.trimStart().startsWith("<"))
            return text.trim().slice(0, 200);
    }
    return withBody(base);
}

/**
 * Fetch CoA versions for a business partner
 */
export const fetchCoAVersions = async (
    store: TAppStore,
    businessPartnerId: string,
    businessPartnerType: TBusinessPartnerType,
    skip: number = 0,
    limit: number = 50,
) => {
    const state = store.getState();
    state.setCoAVersionsLoading();

    try {
        const baseUrl =
            businessPartnerType === "service_provider"
                ? `/api/v1/service-providers/${businessPartnerId}/coa/versions`
                : `/api/v1/portfolio-companies/${businessPartnerId}/coa/versions`;

        const response = await fetch(`${baseUrl}?skip=${skip}&limit=${limit}`, {
            headers: await getAuthHeaders(store),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch versions: ${response.statusText}`);
        }

        const raw = await response.json();
        let data: TPaginatedCoAVersions = {
            items: (raw.items || []).map((v: Record<string, unknown>) => ({
                id: String(v["id"]),
                serviceProviderId:
                    v["service_provider_id"] != null ? String(v["service_provider_id"]) : undefined,
                portfolioCompanyId:
                    v["portfolio_company_id"] != null
                        ? String(v["portfolio_company_id"])
                        : undefined,
                versionNumber: Number(v["version_number"]),
                sourceType: (v["source_type"] as "upload" | "snapshot") || "upload",
                storagePath: v["storage_path"] != null ? String(v["storage_path"]) : null,
                originalFilename:
                    v["original_filename"] != null ? String(v["original_filename"]) : null,
                createdById: String(v["created_by_id"]),
                createdByName: v["created_by_name"] != null ? String(v["created_by_name"]) : null,
                mappingsCount: Number(v["mappings_count"]),
                description: v["description"] != null ? String(v["description"]) : null,
                createdAt: String(v["created_at"]),
            })),
            total: Number(raw.total),
            page: Number(raw.page),
            pageSize: Number(raw.page_size ?? raw.pageSize),
            hasMore: Boolean(raw.has_more ?? raw.hasMore),
        };
        // If we just uploaded a version and the API didn't return it (e.g. timing), keep it in the list
        const lastUploaded = store.getState().coaLastUploadedVersion;
        if (lastUploaded && !data.items.some((i) => i.id === lastUploaded.id)) {
            data = {
                ...data,
                items: [lastUploaded, ...data.items],
                total: data.total + 1,
            };
        }
        if (lastUploaded) {
            store.getState().setCoALastUploadedVersion(null);
        }
        state.setCoAVersionsList(data);
        state.setCoAVersionSelectedBusinessPartnerId(businessPartnerId);
        return data;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to fetch versions";
        state.setCoAVersionsError(message);
        throw error;
    }
};

/**
 * Upload a new CoA mapping version
 */
export const uploadCoAVersion = async (
    store: TAppStore,
    businessPartnerId: string,
    businessPartnerType: TBusinessPartnerType,
    file: File,
    description?: string,
) => {
    const state = store.getState();
    state.setCoAVersionUploadLoading();

    try {
        const formData = new FormData();
        formData.append("file", file);
        if (description) {
            formData.append("description", description);
        }

        const url =
            businessPartnerType === "service_provider"
                ? `/api/v1/service-providers/${businessPartnerId}/coa/upload`
                : `/api/v1/portfolio-companies/${businessPartnerId}/coa/upload`;

        const authHeaders = await getAuthHeaders(store);
        const response = await fetch(url, {
            method: "POST",
            headers: authHeaders,
            body: formData,
        });

        if (!response.ok) {
            const errMessage = await parseUploadErrorResponse(response);
            throw new Error(errMessage);
        }

        const result = normalizeVersionUploadResult(
            (await response.json()) as Record<string, unknown>,
        );

        if (result.success) {
            state.setCoAVersionUploadSuccess(result);
            // Optimistic update: prepend new version to list so it appears immediately in the table
            if (result.versionId != null && result.versionNumber != null) {
                state.setCoAVersionSelectedBusinessPartnerId(businessPartnerId);
                const listState = store.getState().coaVersionsListState;
                const current = listState.status === "fulfilled" ? listState.data : null;
                const newItem: TCoAVersion = {
                    id: result.versionId,
                    versionNumber: result.versionNumber,
                    mappingsCount: result.mappingsCount,
                    sourceType: "upload",
                    storagePath: result.storagePath ?? null,
                    originalFilename: file.name || null,
                    createdById: "",
                    createdByName: null,
                    description: null,
                    createdAt: getNow().toISOString(),
                    ...(businessPartnerType === "service_provider"
                        ? { serviceProviderId: businessPartnerId }
                        : { portfolioCompanyId: businessPartnerId }),
                };
                if (current) {
                    state.setCoAVersionsList({
                        ...current,
                        items: [newItem, ...current.items],
                        total: current.total + 1,
                    });
                } else {
                    state.setCoAVersionsList({
                        items: [newItem],
                        total: 1,
                        page: 1,
                        pageSize: 50,
                        hasMore: false,
                    });
                }
                // So refetch can merge this version if the API omits it (e.g. timing)
                state.setCoALastUploadedVersion(newItem);
            }
        } else {
            state.setCoAVersionUploadError(result.errors.join(", "));
        }

        return result;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to upload version";
        state.setCoAVersionUploadError(message);
        throw error;
    }
};

/**
 * Create a snapshot of current CoA mappings
 */
export const createCoASnapshot = async (
    store: TAppStore,
    businessPartnerId: string,
    businessPartnerType: TBusinessPartnerType,
    description?: string,
) => {
    const state = store.getState();
    state.setCoASnapshotLoading();

    try {
        const url =
            businessPartnerType === "service_provider"
                ? `/api/v1/service-providers/${businessPartnerId}/coa/snapshot`
                : `/api/v1/portfolio-companies/${businessPartnerId}/coa/snapshot`;

        const headers = await getAuthHeaders(store);
        headers["Content-Type"] = "application/json";
        const response = await fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify({ description }),
        });

        if (!response.ok) {
            throw new Error(`Failed to create snapshot: ${response.statusText}`);
        }

        const result = normalizeVersionUploadResult(
            (await response.json()) as Record<string, unknown>,
        );

        if (result.success) {
            state.setCoASnapshotSuccess(result);
        } else {
            state.setCoASnapshotError(result.errors.join(", "));
        }

        return result;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to create snapshot";
        state.setCoASnapshotError(message);
        throw error;
    }
};

/**
 * Restore a CoA mapping version
 */
export const restoreCoAVersion = async (
    store: TAppStore,
    businessPartnerId: string,
    businessPartnerType: TBusinessPartnerType,
    versionId: string,
) => {
    const state = store.getState();
    state.setCoARestoreLoading();

    try {
        const url =
            businessPartnerType === "service_provider"
                ? `/api/v1/service-providers/${businessPartnerId}/coa/versions/${versionId}/restore`
                : `/api/v1/portfolio-companies/${businessPartnerId}/coa/versions/${versionId}/restore`;

        const response = await fetch(url, {
            method: "PUT",
            headers: await getAuthHeaders(store),
        });

        if (!response.ok) {
            throw new Error(`Failed to restore version: ${response.statusText}`);
        }

        const result: TVersionRestoreResult = await response.json();

        if (result.success) {
            state.setCoARestoreSuccess(result);
        } else {
            state.setCoARestoreError(result.errors.join(", "));
        }

        return result;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to restore version";
        state.setCoARestoreError(message);
        throw error;
    }
};
